import React, { useEffect, useId, useRef, useState } from "react";
import { motion, useMotionTemplate, useReducedMotion, useSpring } from "framer-motion";
import { Pause, Play } from "lucide-react";
import AnimatedText from "../AnimatedText";

const services = [
  { title: "Genuine Parts Replacement", description: "Authentic spare parts with expert installation." },
  { title: "Custom Stickering & Labeling", description: "Premium branding and unit identification." },
  { title: "Hauling & Transport Services", description: "Reliable pickup and relocation support." },
  { title: "Professional Installation & Setup", description: "Expert installation, leveling, and performance testing." },
  { title: "Expert Repair & Preventive Maintenance", description: "Timely diagnostics, repairs, and ongoing maintenance." },
];

// The ring holds two copies of the list, so a card only wraps around to the
// other side while it is out of sight below the arc.
const ring = [...services, ...services];
const COUNT = ring.length;
const AUTOPLAY_MS = 3200;

type Geometry = { radius: number; step: number; cardW: number; cardH: number; stageH: number };
const DESKTOP: Geometry = { radius: 900, step: 18, cardW: 250, cardH: 310, stageH: 540 };
const MOBILE: Geometry = { radius: 560, step: 27, cardW: 210, cardH: 270, stageH: 470 };

// Distance of card i from the focused slot, wrapped into [-COUNT/2, COUNT/2).
const offsetOf = (i: number, active: number) => ((((i - active) % COUNT) + COUNT + COUNT / 2) % COUNT) - COUNT / 2;

// Soft tint behind each card's artwork, fading down into the card.
const ART_TINTS = [
  "from-[#e3eafc] dark:from-[#1b2452]",
  "from-[#fcefd2] dark:from-[#3a2c12]",
  "from-[#e3eafc] dark:from-[#1b2452]",
  "from-[#fcefd2] dark:from-[#3a2c12]",
  "from-[#e8e6fb] dark:from-[#231f4d]",
];

type Move = { x?: number[]; y?: number[]; rotate?: number[]; scale?: number[]; opacity?: number[] };
type Fill = (name: string) => string;

// Wraps one element in its own looping motion. `origin` is relative to the
// element's own box, e.g. "50% 0%" to swing from the top.
const Loop: React.FC<{
  on: boolean;
  move: Move;
  duration: number;
  delay?: number;
  linear?: boolean;
  times?: number[];
  origin?: string;
  children: React.ReactNode;
}> = ({ on, move, duration, delay = 0, linear, times, origin = "center", children }) => (
  <motion.g
    style={{ transformBox: "fill-box", transformOrigin: origin }}
    animate={on ? move : {}}
    transition={{ duration, delay, times, repeat: Infinity, ease: linear ? "linear" : "easeInOut" }}
  >
    {children}
  </motion.g>
);

// Gear outline: flat-topped teeth around a solid rim.
const gearPath = (cx: number, cy: number, outer: number, inner: number, teeth: number) => {
  const pts: string[] = [];
  const step = (Math.PI * 2) / teeth;
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    [
      [inner, a],
      [outer, a + step * 0.15],
      [outer, a + step * 0.45],
      [inner, a + step * 0.6],
    ].forEach(([r, t]) => pts.push(`${(cx + r * Math.cos(t)).toFixed(1)},${(cy + r * Math.sin(t)).toFixed(1)}`));
  }
  return `M${pts.join("L")}Z`;
};

const Gear: React.FC<{ u: Fill; cx: number; cy: number; r: number; teeth: number; fill: string }> = ({ u, cx, cy, r, teeth, fill }) => (
  <>
    <path d={gearPath(cx, cy, r, r * 0.8, teeth)} fill={u(fill)} />
    <circle cx={cx} cy={cy} r={r * 0.32} fill={u("ice")} />
    <ellipse cx={cx - r * 0.38} cy={cy - r * 0.42} rx={r * 0.3} ry={r * 0.16} fill={u("hl")} transform={`rotate(-30 ${cx - r * 0.38} ${cy - r * 0.42})`} />
  </>
);

const Ground: React.FC<{ u: Fill; cx: number; rx: number; cy?: number }> = ({ u, cx, rx, cy = 140 }) => (
  <ellipse cx={cx} cy={cy} rx={rx} ry={6} fill={u("shadow")} />
);

// Glossy illustrations of each service; every part runs its own loop.
const Artwork: React.FC<{ variant: number; animate: boolean }> = ({ variant, animate }) => {
  const id = useId().replace(/:/g, "");
  const u: Fill = (n) => `url(#${id}-${n})`;
  const on = animate;

  const scenes = [
    // Genuine Parts Replacement: meshing gears and a bolt.
    <>
      <Ground u={u} cx={96} rx={60} />
      <Loop on={on} move={{ rotate: [0, 360] }} duration={12} linear>
        <Gear u={u} cx={82} cy={80} r={46} teeth={10} fill="navy" />
      </Loop>
      <Loop on={on} move={{ rotate: [0, -360] }} duration={7.2} linear>
        <Gear u={u} cx={146} cy={40} r={26} teeth={8} fill="gold" />
      </Loop>
      <Loop on={on} move={{ y: [0, -6, 0], rotate: [0, 8, 0] }} duration={3.4}>
        {/* Bolt: hex head over a threaded shaft */}
        <rect x="148" y="98" width="12" height="34" rx="3" fill={u("ice")} />
        {[104, 112, 120].map((y) => (
          <rect key={y} x="147" y={y} width="14" height="2.5" rx="1.25" fill="#9fb0d2" />
        ))}
        <path d="M144 86h20l5 7-5 7h-20l-5-7z" fill={u("gold")} />
      </Loop>
    </>,

    // Custom Stickering & Labeling: a label pressed onto a freezer, and a swinging tag.
    <>
      <Ground u={u} cx={98} rx={56} />
      <rect x="46" y="38" width="104" height="96" rx="14" fill={u("ice")} />
      <rect x="46" y="38" width="104" height="20" rx="10" fill={u("navy")} />
      <rect x="86" y="62" width="24" height="4" rx="2" fill="#9fb0d2" />
      <Loop on={on} move={{ scale: [0.4, 1.08, 1, 1, 0.4], rotate: [-18, 2, 0, 0, -18], opacity: [0, 1, 1, 1, 0] }} duration={4} times={[0, 0.2, 0.3, 0.85, 1]}>
        <rect x="64" y="78" width="68" height="40" rx="9" fill={u("gold")} />
        <rect x="74" y="88" width="36" height="6" rx="3" fill="#fff6dd" />
        <rect x="74" y="100" width="24" height="6" rx="3" fill="#fff6dd" opacity="0.8" />
        <path d="M120 78h3a9 9 0 0 1 9 9v3z" fill="#fff3cf" />
      </Loop>
      <Loop on={on} move={{ rotate: [-12, 10, -12] }} duration={2.8} origin="50% 0%">
        <line x1="166" y1="14" x2="166" y2="30" stroke="#9fb0d2" strokeWidth="2" />
        <path d="M156 30h20v26l-10 8-10-8z" fill={u("navy")} />
        <circle cx="166" cy="38" r="3" fill={u("ice")} />
      </Loop>
    </>,

    // Hauling & Transport: a box truck on a moving road.
    <>
      <Ground u={u} cx={100} rx={76} cy={134} />
      <motion.line
        x1="0" y1="138" x2="200" y2="138"
        stroke="#9fb0d2" strokeWidth="3" strokeLinecap="round" strokeDasharray="14 10"
        animate={on ? { strokeDashoffset: [0, 48] } : {}}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      {[40, 58, 76].map((y, i) => (
        <Loop key={y} on={on} move={{ x: [0, -14, 0], opacity: [0.2, 0.8, 0.2] }} duration={1.2} delay={i * 0.2}>
          <rect x={6 + i * 4} y={y} width="20" height="3" rx="1.5" fill="#9fb0d2" />
        </Loop>
      ))}
      <Loop on={on} move={{ y: [0, -1.5, 0] }} duration={0.5}>
        {/* Cargo box */}
        <rect x="30" y="38" width="100" height="70" rx="9" fill={u("ice")} />
        <rect x="30" y="92" width="100" height="8" fill={u("gold")} />
        <path d="M80 52v24M69.6 58l20.8 12M69.6 70l20.8-12" stroke="#172b81" strokeWidth="3.5" strokeLinecap="round" />
        {/* Cab */}
        <path d="M130 58h24c6 0 10 3 13 8l9 14c1.5 2.5 2 5 2 8v14c0 3-2 6-6 6h-42z" fill={u("navy")} />
        <path d="M140 64h13c3 0 5 1.5 6.5 4l6 10h-25.5z" fill="#dfe7f5" />
        <ellipse cx="146" cy="66" rx="6" ry="2.5" fill={u("hl")} />
      </Loop>
      {[58, 152].map((cx) => (
        <Loop key={cx} on={on} move={{ rotate: [0, 360] }} duration={0.8} linear>
          <circle cx={cx} cy="112" r="14" fill="#0f1b52" />
          <circle cx={cx} cy="112" r="6" fill={u("ice")} />
          <rect x={cx - 1.5} y="100" width="3" height="7" rx="1.5" fill="#9fb0d2" />
        </Loop>
      ))}
    </>,

    // Installation & Setup: a spirit level settles on a freezer, then a check appears.
    <>
      <Ground u={u} cx={100} rx={62} />
      <rect x="46" y="66" width="108" height="66" rx="10" fill={u("ice")} />
      <rect x="42" y="56" width="116" height="16" rx="8" fill={u("navy")} />
      <rect x="92" y="80" width="16" height="5" rx="2.5" fill="#9fb0d2" />
      <Loop on={on} move={{ rotate: [-6, 3, -1, 0, 0, -6] }} duration={4} times={[0, 0.2, 0.32, 0.42, 0.9, 1]}>
        <rect x="58" y="30" width="84" height="20" rx="10" fill={u("gold")} />
        <rect x="86" y="34" width="28" height="12" rx="6" fill="#f3f8f1" />
        <line x1="100" y1="34" x2="100" y2="46" stroke="#9fb0d2" strokeWidth="1.5" />
        <Loop on={on} move={{ x: [-8, 5, -2, 0, 0, -8] }} duration={4} times={[0, 0.2, 0.32, 0.42, 0.9, 1]}>
          <circle cx="100" cy="40" r="4" fill="#ffffff" stroke="#c7d2e5" />
        </Loop>
      </Loop>
      <Loop on={on} move={{ scale: [0, 0, 1.15, 1, 1, 0], opacity: [0, 0, 1, 1, 1, 0] }} duration={4} times={[0, 0.42, 0.52, 0.6, 0.9, 1]}>
        <circle cx="160" cy="112" r="15" fill={u("navy")} />
        <path d="M153 112l5 5 9-10" fill="none" stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </Loop>
    </>,

    // Expert Repair & Maintenance: a wrench at work over a diagnostic pulse.
    <>
      <Ground u={u} cx={100} rx={58} />
      <motion.path
        d="M16 126H70L80 110L92 140L104 116L112 126H184"
        fill="none" stroke={u("navy")} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
        initial={false}
        animate={on ? { pathLength: [0, 1, 1], opacity: [1, 1, 0] } : { pathLength: 1, opacity: 1 }}
        transition={{ duration: 2.6, times: [0, 0.7, 1], repeat: Infinity, ease: "easeInOut" }}
      />
      <Loop on={on} move={{ rotate: [-18, 12, -18] }} duration={1.8} origin="50% 85%">
        <mask id={`${id}-jaw`}>
          <rect x="0" y="0" width="200" height="150" fill="white" />
          <rect x="94" y="18" width="16" height="22" rx="3" fill="black" />
        </mask>
        <g mask={`url(#${id}-jaw)`}>
          <circle cx="102" cy="40" r="20" fill={u("gold")} />
          <rect x="94" y="44" width="16" height="62" rx="8" fill={u("gold")} />
        </g>
        <ellipse cx="94" cy="38" rx="5" ry="9" fill={u("hl")} transform="rotate(20 94 38)" />
        <circle cx="102" cy="98" r="3.5" fill="#fff3cf" />
      </Loop>
      <Loop on={on} move={{ rotate: [0, 360] }} duration={9} linear>
        <Gear u={u} cx={156} cy={46} r={18} teeth={8} fill="navy" />
      </Loop>
    </>,
  ];

  return (
    <svg viewBox="0 0 200 150" className="w-full h-full overflow-visible" aria-hidden="true">
      <defs>
        {/* Glossy fills: bright specular spot top-left, deep shade bottom-right */}
        <radialGradient id={`${id}-navy`} cx="0.32" cy="0.28" r="0.85">
          <stop offset="0%" stopColor="#9fb3ff" />
          <stop offset="35%" stopColor="#4f6fd6" />
          <stop offset="75%" stopColor="#172b81" />
          <stop offset="100%" stopColor="#0c184f" />
        </radialGradient>
        <radialGradient id={`${id}-gold`} cx="0.32" cy="0.28" r="0.85">
          <stop offset="0%" stopColor="#fff3cf" />
          <stop offset="35%" stopColor="#f5cd7a" />
          <stop offset="75%" stopColor="#e1ae48" />
          <stop offset="100%" stopColor="#b3801f" />
        </radialGradient>
        <radialGradient id={`${id}-ice`} cx="0.32" cy="0.28" r="0.85">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="55%" stopColor="#eef2f9" />
          <stop offset="100%" stopColor="#b9c6de" />
        </radialGradient>
        <radialGradient id={`${id}-hl`}>
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-shadow`}>
          <stop offset="0%" stopColor="#0f172a" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
        </radialGradient>
      </defs>
      {scenes[variant]}
    </svg>
  );
};

const ArcCard: React.FC<{
  service: (typeof services)[number];
  variant: number;
  offset: number;
  geo: Geometry;
  pivotY: number;
  instant: boolean;
  onSelect: () => void;
}> = ({ service, variant, offset, geo, pivotY, instant, onSelect }) => {
  const focused = offset === 0;
  const distance = Math.abs(offset);
  const angle = useSpring(offset * geo.step, { stiffness: 110, damping: 22 });
  const prevOffset = useRef(offset);

  useEffect(() => {
    // A wrap-around jump happens off-screen, so snap instead of sweeping
    // the card across the whole arc.
    if (instant || Math.abs(offset - prevOffset.current) > 1) angle.jump(offset * geo.step);
    else angle.set(offset * geo.step);
    prevOffset.current = offset;
  }, [offset, geo.step, instant, angle]);

  // Each card pivots around a point far below the stage, which places it on
  // the arc and tilts it to follow the curve.
  const transform = useMotionTemplate`translate(-50%, -50%) rotate(${angle}deg) translateY(${-geo.radius}px)`;

  return (
    <motion.div
      className="absolute left-1/2"
      style={{ top: pivotY, width: geo.cardW, height: geo.cardH, transform, zIndex: 10 - distance }}
    >
      <motion.button
        type="button"
        onClick={onSelect}
        tabIndex={distance <= 2 ? 0 : -1}
        aria-hidden={distance > 2}
        aria-label={focused ? undefined : `Show ${service.title}`}
        className={`w-full h-full !p-2.5 !border-0 !rounded-[28px] text-left flex flex-col overflow-hidden transition-shadow duration-500 ${
          focused
            ? "!bg-white dark:!bg-[#1E1E1E] shadow-[0_30px_60px_-20px_rgba(15,23,42,0.35)] ring-1 ring-black/[0.06] dark:ring-white/10"
            : "!bg-white/90 dark:!bg-[#1a1a1a] shadow-[0_12px_30px_-18px_rgba(15,23,42,0.3)] ring-1 ring-black/[0.04] dark:ring-white/5"
        }`}
        initial={false}
        animate={{ scale: focused ? 1 : 0.86, opacity: distance === 0 ? 1 : distance === 1 ? 0.7 : distance === 2 ? 0.35 : 0 }}
        transition={instant ? { duration: 0 } : { type: "spring", stiffness: 140, damping: 22 }}
      >
        {/* Artwork panel: tinted backdrop fading into the card */}
        <div className={`flex-1 min-h-0 rounded-[20px] bg-gradient-to-b ${ART_TINTS[variant]} to-white dark:to-[#1E1E1E] p-4`}>
          <Artwork variant={variant} animate={distance <= 2 && !instant} />
        </div>
        <div className="px-3.5 pt-4 pb-3">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white leading-snug">{service.title}</h3>
          <motion.p
            className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed overflow-hidden"
            initial={false}
            animate={{ opacity: focused ? 1 : 0, height: focused ? "auto" : 0, marginTop: focused ? 6 : 0 }}
            transition={{ duration: instant ? 0 : 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {service.description}
          </motion.p>
        </div>
      </motion.button>
    </motion.div>
  );
};


const Services: React.FC = () => {
  const prefersReducedMotion = !!useReducedMotion();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(!prefersReducedMotion);
  const [hovered, setHovered] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [small, setSmall] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setSmall(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Background tabs keep timers running; don't let turns pile up while away.
  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!playing || hovered || tabHidden) return;
    const t = setTimeout(() => setActive((a) => a + 1), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [active, playing, hovered, tabHidden]);

  const geo = small ? MOBILE : DESKTOP;
  // The focused card's centre sits near the top of the stage; the pivot is
  // one radius below it.
  const pivotY = 40 + geo.cardH / 2 + geo.radius;
  const focusedService = ((active % services.length) + services.length) % services.length;

  const goToService = (target: number) => {
    let delta = (target - focusedService + services.length) % services.length;
    if (delta > services.length / 2) delta -= services.length;
    setActive((a) => a + delta);
  };

  return (
    <section id="services" className="relative overflow-hidden bg-white dark:bg-[#121212] pt-24 md:pt-32">
      <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
        <p className="text-sm font-semibold text-[#172b81] dark:text-[#e1ae48] mb-3">Our Services</p>
        <AnimatedText
          as={motion.h2}
          className="text-4xl md:text-6xl font-semibold tracking-tight text-slate-900 dark:text-white leading-[1.05] mb-5"
          text="Comprehensive Freezer Solutions"
        />
        <p className="text-lg text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
          We provide end-to-end services to ensure your cold storage needs are met with precision and care.
        </p>

        {/* Pagination + play/pause */}
        <div className="mt-10 inline-flex items-center gap-3">
          <div className="flex items-center gap-2 h-10 px-4 rounded-full bg-slate-100 dark:bg-white/5">
            {services.map((s, i) => (
              <button
                key={s.title}
                type="button"
                onClick={() => goToService(i)}
                aria-label={`Show ${s.title}`}
                aria-current={i === focusedService}
                className={`!p-0 !border-0 h-2 !rounded-full transition-all duration-500 ${
                  i === focusedService ? "w-6 !bg-slate-900 dark:!bg-white" : "w-2 !bg-slate-300 dark:!bg-white/25 hover:!bg-slate-400"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? "Pause carousel" : "Play carousel"}
            className="w-10 h-10 !p-0 !border-0 !rounded-full flex items-center justify-center !bg-slate-100 dark:!bg-white/5 text-slate-900 dark:text-white hover:!bg-slate-200 dark:hover:!bg-white/10 transition-colors"
          >
            {playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" className="translate-x-px" />}
          </button>
        </div>
      </div>

      {/* The arc */}
      <div
        className="relative mt-6 md:mt-10 overflow-hidden"
        style={{ height: geo.stageH }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Guide ring the cards ride along */}
        <div
          aria-hidden="true"
          className="absolute left-1/2 rounded-full border border-slate-200/80 dark:border-white/10 pointer-events-none"
          style={{ top: pivotY, width: geo.radius * 2, height: geo.radius * 2, transform: "translate(-50%, -50%)" }}
        />
        <div
          aria-hidden="true"
          className="absolute left-1/2 rounded-full pointer-events-none bg-[radial-gradient(closest-side,rgba(23,43,129,0.06),transparent)] dark:bg-[radial-gradient(closest-side,rgba(225,174,72,0.06),transparent)]"
          style={{ top: pivotY, width: geo.radius * 2.2, height: geo.radius * 2.2, transform: "translate(-50%, -50%)" }}
        />

        {ring.map((service, i) => (
          <ArcCard
            key={i}
            service={service}
            variant={i % services.length}
            offset={offsetOf(i, active)}
            geo={geo}
            pivotY={pivotY}
            instant={prefersReducedMotion}
            onSelect={() => setActive((a) => a + offsetOf(i, a))}
          />
        ))}

        {/* Soft fade where the arc runs off the bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-[#121212] to-transparent pointer-events-none z-20" />
      </div>
    </section>
  );
};

export default Services;
